import cn from 'classnames';
import React, { useRef, useEffect, useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { shallowEqual } from 'react-redux';

import updateImageIdByUuidOperation from 'editor/src/store/design/operation/updateImageIdByUuidOperation';
import updateMediaElementOperation, {
  MediaUpdateActionName,
} from 'editor/src/store/design/operation/updateMediaElementOperation';
import getImageElementUrl from 'editor/src/store/design/selector/getImageElementUrl';
import { MediaImage, MediaAddon, StructureIndex } from 'editor/src/store/design/types';
import { GalleryImage, ImageState } from 'editor/src/store/gallery/types';
import { useDispatch, useSelector } from 'editor/src/store/hooks';
import getPlugin from 'editor/src/store/plugins/selector/getPlugin';
import { PluginName, PluginState } from 'editor/src/store/plugins/types';

import { defaultQrCodeConfig, QR_TYPE_FIELDS, QR_TYPE_LABELS } from 'editor/src/lib/qr';
import iconInfo from 'editor/src/static/info-icon.svg';

import Accordion from 'editor/src/component/Accordion';
import Button from 'editor/src/component/Button';
import useDragHandle from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useDragHandle';
import useReorganizableListUpdate from 'editor/src/component/ConditionGroupBuilder/ConditionElements/ReorganizableList/useReorganizableListUpdate';
import useOverlay from 'editor/src/component/ConditionGroupBuilder/Overlay/useOverlay';
import ElementName from 'editor/src/component/PersonalizationContent/PersonalizationElements/ElementName';
import useFocusElementOnFlag from 'editor/src/component/PersonalizationContent/PersonalizationElements/useFocusElementOnFlag';
import QrCodeConfig from 'editor/src/component/QrCodeConfig';
import SwitchControl from 'editor/src/component/SwitchControl';
import { UploaderContext } from 'editor/src/component/Uploader/useUploader';
import WithTooltip from 'editor/src/component/WithTooltip';

import useSellerQrImage from './useSellerQrImage';

import styles from './index.module.scss';

interface Props {
  element: MediaImage | MediaAddon;
  address: StructureIndex;
  isSelected: boolean;
  isBuilding?: boolean;
  className?: string;
}

function PersonalizationSimpleImage({ element, address, isSelected, isBuilding, className }: Props) {
  const { imageUrl, crossOrigin } = useSelector((state) => getImageElementUrl(state, element), shallowEqual);
  const lastImageAdded = useSelector((state) => state.gallery.images[0] as GalleryImage | undefined);
  const [isSelectingImage, setIsSelectingImage] = useState(false);
  const [isLoadingImage, setIsLoadingImage] = useState(false);
  const [isImageOptionsCollapsed, setIsImageOptionsCollapsed] = useState(false);
  const [isQrConfigOpen, setIsQrConfigOpen] = useState(false);
  // QR generation is only supported by the in-page personalizer (web-to-print stores).
  const isQrOptionAvailable = useSelector(
    (state) => getPlugin(state, PluginName.inPagePersonalization)?.state === PluginState.ENABLED,
  );
  const dispatch = useDispatch();
  const divRef = useRef<HTMLDivElement>(null);
  const uploader = useContext(UploaderContext);
  const { t } = useTranslation();

  const dragHandle = useDragHandle();

  useEffect(() => {
    if (!isSelectingImage || !lastImageAdded) {
      return;
    }

    const selectedImageUuid = element.uuid;

    if (selectedImageUuid) {
      dispatch(updateImageIdByUuidOperation(selectedImageUuid, lastImageAdded.id, true));
    }

    setIsLoadingImage(lastImageAdded.state !== ImageState.UPLOADED);
    if (lastImageAdded.state === ImageState.UPLOADED) {
      setIsSelectingImage(false);
    }
  }, [lastImageAdded?.id, lastImageAdded?.state]);

  const qrCode = element.type === 'image' ? element.qrCode : undefined;
  useSellerQrImage(element.type === 'image' ? element : undefined, address);

  const updateList = useReorganizableListUpdate();
  useEffect(() => {
    updateList();
    // qrCode / accordion state: the mapping panel expands/collapses this row, so
    // the reorganizable list must re-measure row heights.
  }, [element.name, qrCode, isImageOptionsCollapsed]);

  const imageEditorOverlay = useOverlay();
  const active = imageEditorOverlay.visible || isSelected;
  useFocusElementOnFlag(active, divRef);

  if (element.personalizationLocked) {
    return null;
  }

  function uploadImage() {
    setIsSelectingImage(true);
    uploader.open(() => {
      if (!uploader.isUploading()) {
        setIsSelectingImage(false);
      }
    });
  }

  function onToggleQrCode(on: boolean) {
    // An undo step of its own: auto-placement records none, so this is what Undo returns to.
    dispatch(
      updateMediaElementOperation(
        address,
        { qrCode: on ? defaultQrCodeConfig() : undefined },
        MediaUpdateActionName.UNKNOWN,
      ),
    );
    if (on) {
      setIsQrConfigOpen(true);
    }
  }

  function qrSummary(): string {
    // Defensive: a design could carry a malformed/hand-authored qrCode object.
    if (!qrCode || !qrCode.type || !Array.isArray(qrCode.fields) || !QR_TYPE_FIELDS[qrCode.type]) {
      return '';
    }
    const typeLabel = t(QR_TYPE_LABELS[qrCode.type]);
    const defs = QR_TYPE_FIELDS[qrCode.type];
    if (defs.length === 1) {
      const field = qrCode.fields.find((f) => f.key === defs[0].key);
      let sourceLabel = t('Not configured');
      if (field?.source.type === 'static') {
        sourceLabel = field.source.value || t('Not configured');
      } else if (field?.source.type === 'element') {
        sourceLabel = t('Linked field');
      }
      return `${typeLabel} · ${sourceLabel}`;
    }
    const mappedCount = qrCode.fields.length;
    return `${typeLabel} · ${mappedCount} ${t('fields mapped')}`;
  }

  return (
    <div
      ref={divRef}
      className={cn(className, styles.PersonalizationSimpleImage, 'cy-personalization-image', {
        [styles.building]: isBuilding,
        [styles.active]: active,
        [styles.static]: !isBuilding,
      })}
      onMouseDown={dragHandle.onMouseDown}
    >
      <ElementName element={element} isBuilding={isBuilding} address={address} />
      <div
        className={cn(styles.imageContainer, {
          [styles.isBuilding]: isBuilding,
        })}
      >
        <img
          className={cn(styles.imgPreviewStyle)}
          key={(imageUrl || '') + (crossOrigin || '')} // needed to avoid some CORS issue with safari
          crossOrigin={crossOrigin}
          draggable={false}
          src={imageUrl}
          alt={element.name}
        />
        <Button
          transparent
          className={cn(styles.uploadBtn, 'cy-image-upload-btn')}
          onClick={uploadImage}
          disabled={isLoadingImage}
          showLoader={isLoadingImage}
        >
          {t('Upload')}
        </Button>
      </div>
      {isBuilding && isQrOptionAvailable && element.type === 'image' && (
        <div className={styles.imageOptions}>
          <Accordion
            header={t('Image options')}
            headerClassName={cn(styles.imageOptionsHeader, 'cy-image-options-accordion')}
            collapsed={isImageOptionsCollapsed}
            setCollapsed={setIsImageOptionsCollapsed}
          >
            <div className={styles.qrCodeOption}>
              <div className={styles.switchControl}>
                <div className={styles.qrCodeOptionTitle}>
                  {t('QR code')}
                  <WithTooltip
                    overlay={t(
                      'This image spot shows a QR code. Choose the QR type and map its fields in the configuration dialog — the QR code updates automatically as buyers fill in their details.',
                    )}
                    placement="top"
                  >
                    <img className={styles.qrCodeInfoIcon} src={iconInfo} alt={t('QR code')} draggable={false} />
                  </WithTooltip>
                </div>
                <SwitchControl
                  title=""
                  className="cy-qr-generation-switch"
                  on={!!element.qrCode}
                  disabled={false}
                  onSwitch={onToggleQrCode}
                />
              </div>
              {qrCode && (
                <div className={styles.qrCodeSummaryRow}>
                  <span className={styles.qrCodeSummary}>{qrSummary()}</span>
                  <Button
                    transparent
                    className={cn(styles.qrConfigureBtn, 'cy-qr-configure-btn')}
                    onClick={() => setIsQrConfigOpen(true)}
                  >
                    {t('Configure')}
                  </Button>
                </div>
              )}
            </div>
          </Accordion>
        </div>
      )}
      {isQrConfigOpen && element.type === 'image' && (
        <QrCodeConfig element={element} address={address} onClose={() => setIsQrConfigOpen(false)} />
      )}
    </div>
  );
}

export default React.memo(PersonalizationSimpleImage);
